/**
 * Citation chip
 *
 * Shared favicon-first citation UI with a side-preview source card.
 *
 * Primary responsibilities:
 * - Render compact favicon citation chips
 * - Show richer source context on hover and focus
 * - Support external links or in-page source jumps
 *
 * Dependencies:
 * - Citation helpers
 * - Next image
 */

'use client'

import { ReactNode, useId } from 'react'
import Image from 'next/image'
import { getGoogleFaviconUrl } from '@/lib/favicon'
import {
  CitationSource,
  getCitationSourceHostname,
  getCitationSourceSummary,
  getCitationSourceTitle
} from '@/lib/citations'
import { cn } from '@/lib/utils'

type CitationChipProps = {
  citationNumber: number
  href?: string | null
  source?: CitationSource | null
  scrollTargetId?: string | null
  className?: string
}

type CitationSourceCardProps = {
  citationNumber: number
  source: CitationSource
  id?: string
  className?: string
}

type CitationSourcesListProps = {
  sources: CitationSource[]
  title?: string
  idPrefix?: string | null
  className?: string
  itemClassName?: string
  cardClassName?: string
}

function getTextFromChildren(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }

  if (Array.isArray(children)) {
    return children.map((child) => getTextFromChildren(child)).join('')
  }

  return ''
}

export function getCitationNumber(
  children: ReactNode
): number | null {
  const normalized = getTextFromChildren(children).replace(/\.$/, '').trim()

  if (!/^\d+$/.test(normalized)) {
    return null
  }

  const parsed = Number.parseInt(normalized, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function buildSourceCard(source: CitationSource, citationNumber: number) {
  const hostname = getCitationSourceHostname(source)
  const title = getCitationSourceTitle(source)
  const summary = getCitationSourceSummary(source)
  const faviconUrl = getGoogleFaviconUrl(hostname, 64) || '/favicon.ico'

  return {
    hostname,
    title,
    summary,
    faviconUrl,
    label: `Source ${citationNumber}`
  }
}

// --------------

export function CitationSourceCard({
  citationNumber,
  source,
  id,
  className
}: CitationSourceCardProps) {
  const { faviconUrl, hostname, label, summary, title } = buildSourceCard(
    source,
    citationNumber
  )

  return (
    <div
      id={id}
      role="note"
      className={cn(
        'bg-background/98 border-border/70 w-72 rounded-2xl border p-3 shadow-[0_18px_45px_rgba(0,0,0,0.18)] backdrop-blur-md',
        className
      )}
    >
      <div className="mb-2 flex items-start gap-3">
        <div className="bg-muted/60 flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10">
          <Image
            src={faviconUrl}
            alt=""
            width={36}
            height={36}
            className="size-9 object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-muted-foreground mb-1 text-[10px] font-medium uppercase tracking-[0.18em]">
            {label}
          </div>
          <div className="text-foreground line-clamp-2 text-sm font-semibold leading-snug">
            {title}
          </div>
          <div className="text-muted-foreground mt-1 truncate text-xs">
            {hostname}
          </div>
        </div>
      </div>
      {summary ? (
        <p className="text-muted-foreground line-clamp-4 text-xs leading-relaxed">
          {summary}
        </p>
      ) : null}
    </div>
  )
}

// --------------

export function CitationSourcesList({
  sources,
  title = 'Sources',
  idPrefix,
  className,
  itemClassName,
  cardClassName
}: CitationSourcesListProps) {
  if (sources.length === 0) {
    return null
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-[0.16em]">
        {title}
      </div>
      <div className="space-y-2">
        {sources.map((source, index) => {
          const itemId = idPrefix ? `${idPrefix}-${index}` : undefined

          return (
            <a
              key={`${source.url}-${index}`}
              id={itemId}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('block scroll-mt-4', itemClassName)}
            >
              <CitationSourceCard
                citationNumber={index + 1}
                source={source}
                className={cn('w-full max-w-xl', cardClassName)}
              />
            </a>
          )
        })}
      </div>
    </div>
  )
}

// --------------

export function CitationChip({
  citationNumber,
  href,
  source,
  scrollTargetId,
  className
}: CitationChipProps) {
  const resolvedHref = href || source?.url || '#'
  const cardSource =
    source ||
    (resolvedHref && resolvedHref !== '#'
      ? { url: resolvedHref }
      : null)
  const hasInteractiveTarget = Boolean(scrollTargetId || resolvedHref !== '#')
  const cardId = useId()
  const hostname = cardSource ? getCitationSourceHostname(cardSource) : 'source'
  const faviconUrl = getGoogleFaviconUrl(hostname, 32) || '/favicon.ico'
  const ariaLabel = cardSource
    ? `Source ${citationNumber}: ${getCitationSourceTitle(cardSource)}`
    : `Source ${citationNumber}`

  if (!hasInteractiveTarget || !cardSource) {
    return (
      <span className="ml-1 inline-flex align-super">
        <span
          aria-label={ariaLabel}
          className={cn(
            'bg-primary/15 text-primary inline-flex min-h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-medium',
            className
          )}
        >
          {citationNumber}
        </span>
      </span>
    )
  }

  return (
    <span className="group/citation relative ml-1 inline-flex align-super">
      <a
        href={resolvedHref}
        target={scrollTargetId ? undefined : '_blank'}
        rel={scrollTargetId ? undefined : 'noopener noreferrer'}
        aria-label={ariaLabel}
        aria-describedby={cardId}
        role={scrollTargetId ? 'button' : undefined}
        className={cn(
          'ring-offset-background bg-muted/30 hover:bg-muted/45 focus-visible:ring-ring focus-visible:ring-offset-background inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 shadow-sm transition-all hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          className
        )}
        onClick={(event) => {
          if (!scrollTargetId) {
            return
          }

          event.preventDefault()
          const target = document.getElementById(scrollTargetId)
          target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }}
      >
        <Image
          src={faviconUrl}
          alt=""
          width={20}
          height={20}
          className="size-5 object-cover"
        />
      </a>
      {cardSource ? (
        <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 opacity-0 transition-all duration-150 group-focus-within/citation:block group-focus-within/citation:opacity-100 group-hover/citation:block group-hover/citation:opacity-100">
          <CitationSourceCard
            id={cardId}
            citationNumber={citationNumber}
            source={cardSource}
          />
        </div>
      ) : null}
    </span>
  )
}
